import type { SearchResult } from '../lib/search-result';
import { trpc } from '../lib/trpc';
import { useDebouncedValue } from './useDebouncedValue';

/** Below this, a query matches so much of the workspace that the results are noise. */
export const MIN_QUERY_LENGTH = 2;

const DEBOUNCE_MS = 200;

export interface UseGlobalSearchOptions {
  /** Restrict to one entity type — used by the type-scoped drill-in. */
  types?: SearchResult['entityType'][];
  /** Rows per entity type. The palette groups by type, so this caps each section. */
  perTypeLimit?: number;
  /** Skip querying entirely, e.g. while the palette is closed. */
  enabled?: boolean;
}

export interface GlobalSearchState {
  results: SearchResult[];
  totalCount: number;
  /** True while a query is in flight *and* nothing is on screen yet — drives the skeleton. */
  isLoading: boolean;
  /** True while showing results for an older query than what's currently typed. */
  isStale: boolean;
  /** True once the query is long enough to have been sent at all. */
  isActive: boolean;
}

/**
 * Debounced global search over contacts and tasks, backing the command palette's Results
 * section.
 *
 * Previous results are held on screen while the next request is in flight (`placeholderData`),
 * so typing doesn't blank the list on every keystroke — the palette should never flash empty
 * between characters.
 */
export function useGlobalSearch(
  query: string,
  { types, perTypeLimit = 5, enabled = true }: UseGlobalSearchOptions = {}
): GlobalSearchState {
  const debouncedQuery = useDebouncedValue(query.trim(), DEBOUNCE_MS);
  const isActive = debouncedQuery.length >= MIN_QUERY_LENGTH;

  const searchQuery = trpc.search.list.useQuery(
    { filter: { query: debouncedQuery, types }, perTypeLimit },
    { enabled: enabled && isActive, placeholderData: (prev) => prev }
  );

  const results = isActive ? (searchQuery.data?.items ?? []) : [];

  return {
    results,
    totalCount: isActive ? (searchQuery.data?.totalCount ?? 0) : 0,
    isLoading: isActive && searchQuery.isFetching && results.length === 0,
    // The user has typed past what we've searched for — either mid-debounce or mid-flight.
    isStale: isActive && (debouncedQuery !== query.trim() || searchQuery.isFetching),
    isActive,
  };
}
